"use client"

import type { ReactNode } from "react"

import { AlertTriangle, BellRing, ExternalLink, MessageSquareOff, SkipForward } from "lucide-react"

import type { ConversationEvent } from "@/lib/conversation/conversation-events"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"


/** Exhaustive on purpose: a fifth type must be a compile error, not a bubble. */
export function ConversationEventLine({
  event,
  crmUrl,
}: {
  event: ConversationEvent
  /** The session's lead in the CRM — the agent inbox has one, the unified inbox does not. */
  crmUrl?: string | null
}) {
  switch (event.type) {
    case "session_start":
      return <SessionSeparator text={event.text} crmUrl={crmUrl} />
    case "handover_notified":
      return <HandoverNotifiedNotice reason={event.reason} />
    case "reply_skipped":
      return event.silentHandover
        ? <SilentHandoverNotice reason={event.reason} logId={event.logId} />
        : <AiSkippedNotice reason={event.reason} logId={event.logId} />
    case "send_failed":
      return <SendFailedNotice reason={event.reason} />
  }
}

/**
 * Service events, drawn the same way wherever a conversation is read. Never a
 * bubble — a bubble has an author, which is how «handover_notified» ended up
 * signed «Staff» — and toned by the attention they deserve, not by what happened.
 */
export function SessionSeparator({
  text,
  crmUrl,
}: {
  /** The session's own label, where the record carries one. */
  text: string | null
  crmUrl?: string | null
}) {
  const tUi = useTranslations("Feedback.ConversationEvents")

  return (
    <div className="flex items-center gap-3 my-4">
      <div className="flex-1 border-t" />
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">{text || tUi("newSession")}</span>
        {crmUrl && (
          <a
            href={crmUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={tUi("openSessionLeadInCRM")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <div className="flex-1 border-t" />
    </div>
  )
}

/** The «Escalated» badge marks every attempt; this marks the one that reached someone. */
export function HandoverNotifiedNotice({ reason }: { reason: string | null }) {
  const tUi = useTranslations("Feedback.ConversationEvents")
  return (
    <EventNotice
      tone="notable"
      icon={<BellRing className="h-4 w-4" />}
      lead={tUi("teamNotified")}
    >
      {reason}
    </EventNotice>
  )
}

/** The model called `skip_reply`. Only `ai_skip`-flagged markers reach here. */
export function AiSkippedNotice({ reason, logId }: { reason: string | null; logId: string | null }) {
  const tUi = useTranslations("Feedback.ConversationEvents")
  return (
    <EventNotice
      tone="quiet"
      icon={<SkipForward className="h-4 w-4" />}
      lead={tUi("aiSkipped")}
    >
      {reason}
      <AiLogLink logId={logId} />
    </EventNotice>
  )
}

/**
 * The AI escalated and told the customer nothing. No reply exists to carry the
 * «Escalated» badge, so without this line the thread shows no sign of the handover.
 */
export function SilentHandoverNotice({ reason, logId }: { reason: string | null; logId: string | null }) {
  const tUi = useTranslations("Feedback.ConversationEvents")
  return (
    <EventNotice tone="notable" icon={<MessageSquareOff className="h-4 w-4" />} lead={tUi("silentEscalation")}>
      <span>{reason ? tUi("silentHandoverReason", {reason}) : tUi("silentHandover")}</span>
      <AiLogLink logId={logId} />
    </EventNotice>
  )
}

function AiLogLink({ logId }: { logId: string | null }) {
  const tUi = useTranslations("Feedback.ConversationEvents")

  if (!logId) return null

  return (
    <a
      href={`?page=ai-logs&log_id=${logId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="ml-1.5 inline-flex items-center gap-0.5 hover:text-foreground transition-colors"
      title={tUi("viewAILog")}
    >
      <ExternalLink className="h-2.5 w-2.5" />
      <span><span key={tUi("log")}>{tUi("log")}</span></span>
    </a>
  )
}

/** Delivery-failure notice: the AI reply was generated but the channel refused it. */
export function SendFailedNotice({ reason }: { reason: string | null }) {
  const tUi = useTranslations("Feedback.ConversationEvents")

  return (
    <EventNotice
      tone="attention"
      icon={<AlertTriangle className="h-4 w-4" />}
      lead={tUi("aIReplyWasNotDelivered")}
    >
      {reason || tUi("theChannelRejectedTheMessage")}
    </EventNotice>
  )
}

type EventTone = "quiet" | "notable" | "attention"

function EventNotice({
  icon,
  lead,
  tone,
  children,
}: {
  icon: ReactNode
  /** Always true; the record's own wording follows it. */
  lead: string
  tone: EventTone
  children?: ReactNode
}) {
  const styles = EVENT_TONES[tone]

  return (
    <div className="flex justify-center my-3">
      <div className={cn("flex max-w-xl items-start gap-2 rounded-md border px-3 py-2", styles.surface)}>
        <span className={cn("mt-0.5 shrink-0", styles.icon)}>{icon}</span>
        <p className={cn("text-xs", styles.body)}>
          <span className={cn("font-medium", styles.lead)}>{lead}</span> {children}
        </p>
      </div>
    </div>
  )
}

/** Rose, not red: red is the destructive colour and the «Escalated» badge already uses it. */
const EVENT_TONES: Record<EventTone, { surface: string; icon: string; body: string; lead: string }> = {
  quiet: {
    surface: "border-border bg-muted/60",
    icon: "text-muted-foreground",
    body: "text-muted-foreground",
    lead: "text-foreground",
  },
  notable: {
    surface: "border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/40",
    icon: "text-rose-600 dark:text-rose-400",
    body: "text-rose-700/80 dark:text-rose-300/80",
    lead: "text-rose-700 dark:text-rose-300",
  },
  attention: {
    surface: "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40",
    icon: "text-amber-600 dark:text-amber-400",
    body: "text-amber-800 dark:text-amber-300",
    lead: "text-amber-800 dark:text-amber-300",
  },
}
